import React, {useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';

/**
 * This modal allows the user to choose an archive purpose for archiving results from the results table.
 */
export interface ResultsArchiveModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (archivePurpose: string) => void;
  numResults: number;
  // This prop will default to BenchlingConfig.ARCHIVE_RUN_AND_RESULT_PURPOSES and is only exposed for
  // testing purposes.
  archivePurposeOptions?: Array<string>;
}

export function ResultsArchiveModal({
  onRequestClose,
  onRequestSubmit,
  numResults,
  archivePurposeOptions = BenchlingConfig.ARCHIVE_RUN_AND_RESULT_PURPOSES,
}: ResultsArchiveModalProps): JSX.Element {
  assert(
    archivePurposeOptions.length > 0,
    'Config ARCHIVE_RUN_AND_RESULT_PURPOSES has not been initialized with archive purposes.'
  );
  const [archivePurpose, setArchivePurpose] = useState<string>(archivePurposeOptions[0]);
  const optionDatas: Array<OptionData> = archivePurposeOptions.map((purpose: string) => ({
    name: purpose,
    value: purpose,
  }));

  return (
    <Modal
      onRequestClose={onRequestClose}
      closeableOnBackgroundClick={true}
      closeableOnEscapeKey={true}
      title="Archive result records"
      onRequestSubmit={() => {
        onRequestSubmit(archivePurpose);
      }}
      buttons={[
        {
          text: 'Archive',
          style: ButtonStyle.NEGATIVE,
          submit: true,
          disabled: false,
          dataTestAttrs: {component: 'ResultsArchiveModal', element: 'archiveButton'},
        },
      ]}
    >
      <div className="modal-body">
        <Row>
          <Col md={12}>
            <p>
              {numResults} {pluralize(numResults, 'result record', 'result records')} will be archived
            </p>
          </Col>
        </Row>
        <div>
          <Row>
            <Col md={12}>
              <FieldLabel label="Mark as" />
            </Col>
          </Row>
          <Row className="u-vertical-center-items">
            <Col md={12}>
              <SelectDropdown
                displayName={archivePurpose}
                menu={
                  <SearchableMenu
                    onSelect={setArchivePurpose}
                    optionDatas={optionDatas}
                    selectedValue={archivePurpose}
                  />
                }
              />
            </Col>
          </Row>
        </div>
      </div>
    </Modal>
  );
}
